import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Paper, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

// Student-facing general-question channel: ask your MENTOR or the OFFICE a
// question (fair eligibility, deadlines, etc.) — separate from the per-submission
// review conversation. Backed by student_message + student-send-message.

interface Msg {
  id:         string
  direction:  'student_to_staff' | 'staff_to_student'
  routed_to:  'mentor' | 'office'
  body:       string
  created_at: string
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function StudentMessagesPage() {
  const { role, loading: authLoading } = useAuth()
  const [msgs, setMsgs]       = useState<Msg[]>([])
  const [loading, setLoading] = useState(true)
  const [routedTo, setRoutedTo] = useState<'mentor' | 'office'>('mentor')
  const [body, setBody]       = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError]     = useState<string | null>(null)

  const load = useCallback(async () => {
    const { data, error: e } = await supabase
      .from('student_message')
      .select('id, direction, routed_to, body, created_at')
      .order('created_at', { ascending: true })
    if (e) setError(e.message)
    else setMsgs((data ?? []) as Msg[])
    setLoading(false)
  }, [])

  useEffect(() => { if (role === 'student') load() }, [role, load])

  if (!authLoading && role !== 'student') {
    return <Box sx={{ p: 4 }}><Alert severity="info">This page is for students.</Alert></Box>
  }

  async function send() {
    const text = body.trim()
    if (!text || sending) return
    setSending(true); setError(null)
    try {
      const { data, error: e } = await supabase.functions.invoke('student-send-message', {
        body: { routed_to: routedTo, body: text },
      })
      if (e) throw new Error(e.message)
      if (data?.error) throw new Error(data.error)
      setBody('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send — please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Messages</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ask your mentor or the Ardent office a question — about fairs, deadlines, or anything that isn't part of a
        submission. For feedback on work you've submitted, use the conversation on that step instead.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Send a question to:</Typography>
        <ToggleButtonGroup
          exclusive size="small" value={routedTo} sx={{ mb: 1.5 }}
          onChange={(_, v) => { if (v) setRoutedTo(v) }}
        >
          <ToggleButton value="mentor">My mentor</ToggleButton>
          <ToggleButton value="office">The office</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          fullWidth multiline minRows={3} maxRows={12} size="small"
          placeholder={routedTo === 'office'
            ? "e.g. As a homeschool student, am I eligible for the county fair? Which one should I enter first?"
            : "e.g. A question about my project direction or the next step…"}
          value={body} onChange={e => setBody(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Button variant="contained" onClick={send} disabled={sending || !body.trim()}>
          {sending ? <CircularProgress size={18} color="inherit" /> : 'Send'}
        </Button>
      </Paper>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Your conversation</Typography>
      {loading ? (
        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
      ) : msgs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {msgs.map(m => {
            const mine = m.direction === 'student_to_staff'
            return (
              <Box key={m.id} sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                <Box sx={{
                  maxWidth: '80%', px: 1.75, py: 1.25, borderRadius: 2,
                  bgcolor: mine ? 'primary.main' : 'action.hover',
                  color: mine ? 'primary.contrastText' : 'text.primary',
                }}>
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mb: 0.25 }}>
                    {mine ? `You → ${m.routed_to === 'office' ? 'the office' : 'your mentor'}` : 'Ardent staff'} · {fmt(m.created_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{m.body}</Typography>
                </Box>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}
